/* eslint-disable no-restricted-syntax */
import { createAsyncThunk, createSlice, current } from '@reduxjs/toolkit';

import { STATUS } from 'projectConstants';
import apiService from 'services';
import convertToDate from 'utils/getMonthYear';
import flattenIndicatorOutputsForTable from 'utils/flattenIndicatorOutputsForTable';
import patchDimensionalIndicatorsFromUpdate from 'pages/BuilderMode/dataTableV2/patchDimensionalIndicatorsFromUpdate';
import resolveAddIndicatorForDataTable from 'pages/BuilderMode/dataTableV2/resolveAddIndicatorForDataTable';
import mapAddIndicatorResponseToBlockMetadata, {
  mergeIndicatorPatchResponseIntoBlockMetadata,
} from 'utils/mapAddIndicatorResponseToBlockMetadata';
import {
  findOutputRowForIndicatorId,
  getToolbarIndicatorType,
  resolveIndicatorIdFromRowSelection,
  resolveSelectedIndicatorFromMetadata,
} from 'utils/resolveSelectedIndicatorFromMetadata';
import { enrichIndicatorPatchPayload } from 'utils/enrichIndicatorPatchPayload';

import type { BuilderBlockMetadataResponse } from 'types/builderBlockMetadata';

export const FetchPlannerModeModel = createAsyncThunk(
  'plannerModeModel/get',
  async (modelId: number, thunkAPI) => {
    try {
      const response = await apiService.get_model(modelId);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.response?.data);
    }
  },
);

const getCurrentScenarioIdFromState = (state: any): number | undefined =>
  state?.plannerModeSlice?.baseScenario?.id ??
  state?.builderModeSlice?.baseScenario?.id ??
  state?.dashboardSliceV2?.selectedDashboardScenario?.id ??
  state?.scenario?.baseScenario?.id;

export const FetchFormulaList = createAsyncThunk(
  'formulaList/get',
  async (payload: any, thunkAPI) => {
    try {
      const modelId = payload?.modelId ?? payload;
      const scenarioId = payload?.scenarioId ?? getCurrentScenarioIdFromState(thunkAPI.getState());
      const response = await apiService.get_model_formula_list(modelId, scenarioId);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.response?.data);
    }
  },
);

export const FetchDataInput = createAsyncThunk(
  'FetchDataInput/get',
  async ({ indicator_id, parameters }: any, thunkAPI) => {
    try {
      const response = await apiService.get_indicator_data_input(indicator_id, {
        params: parameters,
      });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.response?.data);
    }
  },
);

export const FetchPlannerModeBlock = createAsyncThunk(
  'plannerModeBlock/get',
  async (blockId: number, thunkAPI) => {
    try {
      const response = await apiService.get_block(blockId);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.response?.data);
    }
  },
);

// Metadata fetch (same API used in BuilderModeSlice), kept inside PlannerModeSlice
// so Planner flows can bootstrap from metadata before loading outputs.
export const FetchPlannerBlockMetadata = createAsyncThunk(
  'plannerMode/blockMetadata/fetch',
  async (
    { blockId, parameters }: { blockId: number; parameters?: Record<string, unknown> },
    thunkAPI,
  ) => {
    try {
      const response = await apiService.get_block_metadata(blockId, {
        params: parameters,
      });
      return response.data as BuilderBlockMetadataResponse;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const FetchPlannerModeItems = createAsyncThunk(
  'plannerModeItems/fetch',
  async ({ dimensionId, params, actuals }: any, thunkAPI) => {
    try {
      const response = await apiService.get_dimension_items(dimensionId, { params });
      return { ...response.data, actuals };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const FetchBlockOutputs = createAsyncThunk(
  'blockOutputs/fetch',
  async ({ blockId, params }: any, thunkAPI) => {
    try {
      const response = await apiService.get_block_outputs(blockId, { params });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

// V2 variant (kept separate so legacy flow doesn't break)
export const FetchBlockOutputsV2 = createAsyncThunk(
  'blockOutputs_v2/fetch',
  async ({ blockId, params, useArrowResponse }: any, thunkAPI) => {
    try {
      if (useArrowResponse) {
        try {
          const { parseBlockOutputsMultipart } = await import(
            'pages/BuilderMode/dataTableV2/parseBlockOutputsMultipart'
          );
          const { setBuilderBlockDetailTable, clearBuilderBlockDetailTable } = await import(
            'pages/BuilderMode/dataTableV2/builderBlockDetailStore'
          );
          const { clearAllBuilderArrowDimWindows } = await import(
            'pages/BuilderMode/dataTableV2/builderArrowDetailWindowStore'
          );
          const { tableFromIPC } = await import('apache-arrow');
          const arrowParameters = {
            ...params,
            response_format: 'arrow',
          };
          const response = await apiService.get_block_outputs_V2_arrow(blockId, {
            params: arrowParameters,
          });
          const contentType = response.headers['content-type'] as string;
          const parsed = parseBlockOutputsMultipart(response.data as ArrayBuffer, contentType);
          const scenarioId = Number(params?.scenario_id);
          if (!Number.isNaN(scenarioId)) {
            clearAllBuilderArrowDimWindows();
            clearBuilderBlockDetailTable(blockId, scenarioId);
            const detail = parsed.manifest.detail as Record<string, unknown> | undefined;
            setBuilderBlockDetailTable(blockId, scenarioId, tableFromIPC(parsed.arrowPart), {
              indicatorColumn: (detail?.indicator_column as string) ?? 'Indicators',
              dimColumns: (detail?.dim_columns as string[]) ?? [],
              numRows: Number(detail?.num_rows ?? 0),
            });
          }
          return parsed.manifest.indicators ?? [];
        } catch {
          // Local/stale API without multipart Arrow — keep builder usable on JSON v2.
          const response = await apiService.get_block_outputs_V2(blockId, { params });
          return response.data;
        }
      }
      const response = await apiService.get_block_outputs_V2(blockId, { params });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const FetchBlockDataValues = createAsyncThunk(
  'blockDataValues/fetch',
  async ({ blockId, params }: any, thunkAPI) => {
    try {
      const response = await apiService.get_block_data_values(blockId, { params });
      return response.data?.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const UpdateIndicator = createAsyncThunk(
  'indicator/patch',
  async (payload: any, thunkAPI) => {
    try {
      const response = await apiService.update_indicator(payload?.indicatorId, payload?.data);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.response?.data?.message);
    }
  },
);

export const UpdateIndicatorFormat = createAsyncThunk(
  'indicatorFormat/patch',
  async (payload: any, thunkAPI) => {
    try {
      const response = await apiService.update_indicator(payload?.indicatorId, payload?.data);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.response?.data?.message);
    }
  },
);

export const UpdateIndicatorProperties = createAsyncThunk(
  'indicatorProperties/patch',
  async ({ indicatorId, data }: any, thunkAPI) => {
    try {
      const response = await apiService.update_indicator(indicatorId, data);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.response?.data?.message);
    }
  },
);

export const UpdateIndicatorFormula = createAsyncThunk(
  'indicatorFormula/patch',
  async ({ indicatorId, data }: any, thunkAPI) => {
    try {
      const response = await apiService.update_indicator(indicatorId, data);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.response?.data?.message);
    }
  },
);

export const DuplicateIndicator = createAsyncThunk(
  'duplicateIndicator/post',
  async ({ indicatorId, data }: any, thunkAPI) => {
    try {
      const response = await apiService.duplicate_indicator(indicatorId, data);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.response?.data?.message);
    }
  },
);

export const UpdateBlockDimension = createAsyncThunk(
  'blockDimension/post',
  async ({ blockId, request_body }: any, thunkAPI) => {
    try {
      const response = await apiService.block_dimension(blockId, request_body);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const FetchScenarios = createAsyncThunk(
  'fetchScenarios/fetch',
  async (modelId: any, thunkAPI) => {
    try {
      const response = await apiService.get_scenarios(modelId);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const RenameScenario = createAsyncThunk(
  'scenarioRename/fetch',
  async (data: any, thunkAPI) => {
    try {
      const response = await apiService.renameScenario(data?.item_id, data.requestBody);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.response?.data);
    }
  },
);

export const DeleteScenarios = createAsyncThunk(
  'scenarioDelete/fetch',
  async (scenarioId: number, thunkAPI) => {
    try {
      const response = await apiService.deleteScenario(scenarioId);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const CreateScenarios = createAsyncThunk(
  'scenarioCreate/fetch',
  async (data: any, thunkAPI) => {
    try {
      const response = await apiService.createScenario(data?.modelId, data.data);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const DuplicateScenarios = createAsyncThunk(
  'scenarioDuplicate/fetch',
  async (data: any, thunkAPI) => {
    try {
      const response = await apiService.createDuplicateScenario(data?.scenarioId, data.data);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const ReorderIndicator = createAsyncThunk(
  'reorderIndicator/fetch',
  async ({ blockId, data }: any, thunkAPI) => {
    try {
      const response = await apiService.reorder_indicator(blockId, data);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const FetchSingleScenarios = createAsyncThunk(
  'singleScenario/fetch',
  async (scenarioId: number, thunkAPI) => {
    try {
      const response = await apiService.getSingleScenario(scenarioId);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const AddIndicator = createAsyncThunk('addIndicator/post', async (data: any, thunkAPI) => {
  try {
    const response = await apiService.add_indicator(data?.blockId, data.data);
    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error?.response?.data);
  }
});

export const DeleteIndicator = createAsyncThunk(
  'indicator/delete',
  async ({ indicatorId }: any, thunkAPI) => {
    try {
      const response = await apiService.delete_Indicator(indicatorId);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const GetDependencyIndicator = createAsyncThunk(
  'indicator/delete',
  async ({ indicatorId }: any, thunkAPI) => {
    try {
      const response = await apiService.get_indicator_dependency(indicatorId);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

interface PlannerModeSliceInterface {
  plannerModeSlice: PlannerModeInterface;
}

interface PlannerModeInterface {
  plannerMode: boolean;
  model: any;
  block: any;
  blockMetadata: BuilderBlockMetadataResponse | null;
  blockMetadataBlockId: number | null;
  blockMetadataScenarioId: number | null;
  blockMetadataLoading: boolean;
  blockMetadataError: string | null;
  indicatorHeader: any;
  dimensionalIndicators: any;
  indicatorsData: any;
  dimensionalIndicatorsV2: any;
  indicatorsDataV2: any;
  selectedCell: any;
  addIndicator: boolean;
  selectedIndicators: any;
  toggleInputPanel: boolean;
  toolbarType: string;
  indicators: any[];
  indicatorsV2: any[];
  scenarioFetchStatus: any;
  blockScenarioMap: any;
  modelId: any;
  blockId: null;
  baseScenario: any;
  scenarioList: any;
  scenarioListStatus: any;
  selectedSceanrioId: any;
  isIndicatorAvailabe: boolean;
  formulaBarChange: any;
  formulaList: any;
  scenarios: any[];
  dataInput: null;
  isExpand: boolean;
  isDependencyArray: any;
  blockOutputsLoading: boolean;
  blockOutputsError: string | null;
  blockOutputsLoadingV2: boolean;
  blockOutputsErrorV2: string | null;
}

const initialState: PlannerModeInterface = {
  plannerMode: localStorage.getItem('BUILDER_MODE') === 'true',
  model: null,
  block: null,
  blockMetadata: null,
  blockMetadataBlockId: null,
  blockMetadataScenarioId: null,
  blockMetadataLoading: false,
  blockMetadataError: null,
  indicatorHeader: null,
  dimensionalIndicators: null,
  indicatorsData: null,
  dimensionalIndicatorsV2: null,
  indicatorsDataV2: null,
  selectedCell: null,
  selectedIndicators: null,
  addIndicator: false,
  toggleInputPanel: false,
  toolbarType: 'Data',
  indicators: [],
  indicatorsV2: [],
  modelId: null,
  blockId: null,
  scenarioFetchStatus: STATUS.IDLE,
  blockScenarioMap: {},
  baseScenario: null,
  scenarioList: null,
  scenarioListStatus: null,
  selectedSceanrioId: null,
  isIndicatorAvailabe: false,
  formulaBarChange: '',
  scenarios: [],
  formulaList: null,
  dataInput: null,
  isExpand: false,
  isDependencyArray: null,
  blockOutputsLoading: false,
  blockOutputsError: null,
  blockOutputsLoadingV2: false,
  blockOutputsErrorV2: null,
};

/** Immer `current()` throws on null/undefined — property tab has no V2 outputs yet. */
function draftArraySnapshot<T>(value: T[] | null | undefined): T[] {
  if (value == null) return [];
  return current(value);
}

function syncSelectedIndicatorFromMetadata(
  state: PlannerModeInterface,
  indicatorId: number | null,
  outputRowOverride?: Record<string, any> | null,
) {
  if (indicatorId == null) {
    state.selectedIndicators = null;
    return;
  }
  const outputRow =
    outputRowOverride ??
    findOutputRowForIndicatorId(indicatorId, state.indicatorsV2, state.indicators);
  state.selectedIndicators = resolveSelectedIndicatorFromMetadata(
    state.blockMetadata,
    indicatorId,
    outputRow,
  );
}

/** Merge PATCH indicator API response into metadata + V2 data table state (no legacy outputs refetch). */
function applyIndicatorPatchToState(
  state: PlannerModeInterface,
  payload: Record<string, any> | undefined,
  indicatorIdFromRequest?: number | string,
) {
  if (!payload) return;

  const patchId = Number(payload.id ?? indicatorIdFromRequest);
  if (Number.isNaN(patchId)) return;

  if (state.blockMetadata?.indicators?.length) {
    const metaIndex = state.blockMetadata.indicators.findIndex(
      (indicator: { id: number }) => Number(indicator.id) === patchId,
    );
    if (metaIndex !== -1) {
      state.blockMetadata.indicators[metaIndex] = mergeIndicatorPatchResponseIntoBlockMetadata(
        state.blockMetadata.indicators[metaIndex],
        payload,
      );
    }
  }

  if (state.dimensionalIndicatorsV2?.length) {
    state.dimensionalIndicatorsV2 = patchDimensionalIndicatorsFromUpdate(
      state.dimensionalIndicatorsV2,
      patchId,
      payload,
    );
  }

  const format = payload.data_format ?? payload.format;
  const indicatorType = payload.indicator_type ?? payload.type;

  if (state.indicatorsV2?.length) {
    state.indicatorsV2 = state.indicatorsV2.map((row: any) => {
      if (Number(row?.id) !== patchId) return row;
      return {
        ...row,
        ...(payload.name != null ? { name: payload.name } : {}),
        ...(format != null ? { type: format, format } : {}),
        ...(payload.data_type != null ? { data_type: payload.data_type } : {}),
        ...(indicatorType != null ? { indicator_type: indicatorType } : {}),
        ...(payload.warning != null ? { warning: payload.warning } : {}),
        ...(payload.help != null ? { help: payload.help } : {}),
        ...(payload.formula != null ? { formula: payload.formula } : {}),
        ...(payload.chart_type != null ? { chart_type: payload.chart_type } : {}),
        ...(payload.output_format != null ? { output_format: payload.output_format } : {}),
        ...(payload.favourite != null ? { is_favourite: payload.favourite } : {}),
        ...(payload.data_format_properties != null
          ? { format_properties: payload.data_format_properties }
          : {}),
      };
    });
  }

  if (state.indicatorsDataV2?.length) {
    const dataIndex = state.indicatorsDataV2.findIndex(
      (entry: any) => Number(entry?.id) === patchId,
    );
    if (dataIndex !== -1) {
      state.indicatorsDataV2[dataIndex] = {
        ...state.indicatorsDataV2[dataIndex],
        ...(payload.name != null ? { 'Block indicators': payload.name } : {}),
        ...(format != null ? { Format: format, type: format } : {}),
        ...(payload.data_type != null ? { 'Roll up type': payload.data_type } : {}),
        ...(indicatorType != null ? { Type: indicatorType, indicator_type: indicatorType } : {}),
        ...(payload.formula != null ? { Formula: payload.formula ?? '' } : {}),
        ...(payload.help != null ? { 'Help text': payload.help ?? '' } : {}),
      };
    }
  }

  if (Number(state.selectedIndicators?.id) === patchId) {
    syncSelectedIndicatorFromMetadata(state, patchId);
  }
}

const PlannerModeSlice = createSlice({
  name: 'plannerModeSlice',
  initialState,

  reducers: {
    handlePlannerMode: (state, action) => {
      localStorage.setItem('BUILDER_MODE', action.payload);
      state.plannerMode = action.payload;
    },
    handleToggleInputPanel: (state) => {
      state.toggleInputPanel = !state.toggleInputPanel;
    },
    toggleToolbarType: (state, action) => {
      state.toolbarType = action.payload;
    },
    handleAddIndicator: (state, action) => {
      state.addIndicator = action.payload;
    },
    handleSelectedCell: (state, action) => {
      state.selectedCell = action.payload;
      const { payload } = action;
      const indicators = draftArraySnapshot(state.indicators);
      const indicatorsV2 = draftArraySnapshot(state.indicatorsV2);
      const dimensionalIndicatorsV2 = draftArraySnapshot(state.dimensionalIndicatorsV2);

      let rowId = payload?.rowId ?? payload?.id;
      if (typeof rowId === 'string' && rowId.includes('_')) {
        const parsedRowId = Number(rowId.split('_')[1]);
        if (!Number.isNaN(parsedRowId)) rowId = parsedRowId;
      }

      const resolvedFromRow = resolveIndicatorIdFromRowSelection(
        rowId,
        dimensionalIndicatorsV2,
        indicatorsV2,
        indicators,
      );
      const fallbackId = Number(payload?.parentId ?? payload?.indicatorId ?? rowId);
      const indicatorId =
        resolvedFromRow ?? (Number.isNaN(fallbackId) || fallbackId <= 0 ? null : fallbackId);
      syncSelectedIndicatorFromMetadata(state, indicatorId, payload);
    },
    handleSelectedCellLocally: (state, action) => {
      const { payload } = action;
      const indicators = draftArraySnapshot(state.indicators);
      const indicatorsV2 = draftArraySnapshot(state.indicatorsV2);
      const dimensionalIndicatorsV2 = draftArraySnapshot(state.dimensionalIndicatorsV2);
      const resolvedFromRow = resolveIndicatorIdFromRowSelection(
        payload?.rowId ?? payload?.id,
        dimensionalIndicatorsV2,
        indicatorsV2,
        indicators,
      );
      const fallbackId = Number(payload?.parentId ?? payload?.indicatorId ?? payload?.id);
      const indicatorId =
        resolvedFromRow ?? (Number.isNaN(fallbackId) || fallbackId <= 0 ? null : fallbackId);
      syncSelectedIndicatorFromMetadata(state, indicatorId, payload);
      // The formula bar's Excel-style name box reads the focused cell's
      // reference from here. This reducer otherwise only derives
      // `selectedIndicators`, so without this the reference was computed in
      // the grid and then thrown away.
      state.selectedCell = {
        ...state.selectedCell,
        rowId: payload?.rowId ?? null,
        columnId: payload?.columnId ?? null,
        cellReference: payload?.cellReference ?? '',
        cellIndicatorName: payload?.cellIndicatorName ?? '',
      };
    },
    handleLocallyAddIndicator: (state, action) => {
      const indicators = draftArraySnapshot(state.indicators);
      const data = [...indicators, action.payload];
      state.indicators = data;
    },
    currentScenario: (state, action) => {
      state.selectedSceanrioId = action.payload;
    },
    indicatorAvailable: (state, action) => {
      state.isIndicatorAvailabe = action.payload;
    },
    selectBaseScenario: (state, action) => {
      state.baseScenario = action.payload;
      state.blockScenarioMap[`${state.modelId}`] = state.baseScenario?.id;
    },
    setExpand: (state, action) => {
      state.isExpand = action.payload;
    },
    resetBuilderDimension: (state) => {
      state.model = null;
      state.block = null;
    },
    /** Legacy JSON dim rows only; Arrow path uses render-time merge (builderArrowOutputs). */
    injectBuilderArrowDimChildren: (
      state,
      action: { payload: { parentId: number; children: Record<string, unknown>[] } },
    ) => {
      const { parentId, children } = action.payload;
      const existing = state.dimensionalIndicatorsV2 ?? [];
      const withoutChildren = existing.filter(
        (row: { parentId?: number }) => Number(row?.parentId) !== parentId,
      );
      const parentIndex = withoutChildren.findIndex(
        (row: { id?: number; chevronType?: string }) =>
          Number(row?.id) === parentId && row?.chevronType === 'parent',
      );
      if (parentIndex < 0) return;
      state.dimensionalIndicatorsV2 = [
        ...withoutChildren.slice(0, parentIndex + 1),
        ...children,
        ...withoutChildren.slice(parentIndex + 1),
      ];
    },
    appendBuilderArrowDimChildren: (
      state,
      action: { payload: { parentId: number; children: Record<string, unknown>[] } },
    ) => {
      const { parentId, children } = action.payload;
      if (children.length === 0) return;
      const existing = state.dimensionalIndicatorsV2 ?? [];
      const parentIndex = existing.findIndex(
        (row: { id?: number; chevronType?: string }) =>
          Number(row?.id) === parentId && row?.chevronType === 'parent',
      );
      if (parentIndex < 0) return;

      let insertAt = parentIndex + 1;
      while (
        insertAt < existing.length &&
        Number((existing[insertAt] as { parentId?: number })?.parentId) === parentId
      ) {
        insertAt += 1;
      }

      state.dimensionalIndicatorsV2 = [
        ...existing.slice(0, insertAt),
        ...children,
        ...existing.slice(insertAt),
      ];
    },
    resetBlockTableData: (state) => {
      state.blockMetadata = null;
      state.blockMetadataBlockId = null;
      state.blockMetadataScenarioId = null;
      state.blockMetadataLoading = false;
      state.blockMetadataError = null;
      state.dimensionalIndicatorsV2 = null;
      state.indicatorsV2 = [];
      state.indicatorsDataV2 = null;
      state.blockOutputsLoadingV2 = false;
      state.blockOutputsErrorV2 = null;
      state.dimensionalIndicators = null;
      state.indicators = [];
      state.indicatorsData = null;
      state.blockOutputsLoading = false;
      state.blockOutputsError = null;
      state.selectedCell = null;
      state.selectedIndicators = null;
      state.addIndicator = false;
    },
    ResetTable: (state) => {
      state.model = null;
      state.block = null;
      state.scenarios = [];
      state.indicators = [];
      state.selectedCell = null;
      // state.baseScenario = null;
      state.scenarioList = null;
      state.toolbarType = 'Data';
      state.addIndicator = false;
      state.indicatorsData = null;
      state.formulaBarChange = '';
      state.indicatorHeader = null;
      state.toggleInputPanel = false;
      state.scenarioListStatus = null;
      state.selectedSceanrioId = null;
      state.selectedIndicators = null;
      state.selectedSceanrioId = null;
      state.dimensionalIndicators = null;
      state.isIndicatorAvailabe = false;
      state.formulaList = null;
      state.dataInput = null;
    },
  },

  extraReducers(builder) {
    builder
      .addCase(FetchPlannerModeModel.fulfilled, (state, action) => {
        state.model = action.payload;
      })
      .addCase(FetchPlannerBlockMetadata.pending, (state, action) => {
        const nextBlockId = action.meta?.arg?.blockId as number | undefined;
        const nextScenarioId = action.meta?.arg?.parameters?.scenario_id as number | undefined;
        const blockChanged = nextBlockId != null && state.blockMetadataBlockId !== nextBlockId;
        const scenarioChanged =
          nextScenarioId != null && state.blockMetadataScenarioId !== nextScenarioId;
        if (blockChanged || scenarioChanged) {
          state.blockMetadata = null;
          state.blockMetadataBlockId = null;
          state.blockMetadataScenarioId = null;
          state.dimensionalIndicatorsV2 = null;
          state.indicatorsV2 = [];
          state.indicatorsDataV2 = null;
          state.blockOutputsLoadingV2 = false;
          state.blockOutputsErrorV2 = null;
        }
        state.blockMetadataLoading = true;
        state.blockMetadataError = null;
      })
      .addCase(FetchPlannerBlockMetadata.rejected, (state, action: any) => {
        state.blockMetadataLoading = false;
        state.blockMetadataError =
          action?.payload || action?.error?.message || 'Failed to fetch block metadata';
      })
      .addCase(FetchPlannerBlockMetadata.fulfilled, (state, action) => {
        state.blockMetadataLoading = false;
        state.blockMetadataError = null;
        state.blockMetadata = action.payload;
        state.blockMetadataBlockId = (action.meta?.arg?.blockId as number | undefined) ?? null;
        state.blockMetadataScenarioId =
          (action.meta?.arg?.parameters?.scenario_id as number | undefined) ?? null;

        const selectedId = Number(state.selectedIndicators?.id);
        if (!Number.isNaN(selectedId) && selectedId > 0) {
          syncSelectedIndicatorFromMetadata(state, selectedId);
        }
      })
      .addCase(FetchDataInput.fulfilled, (state, action) => {
        state.dataInput = action?.payload;
      })
      .addCase(FetchScenarios.fulfilled, (state, action) => {
        state.scenarios = action.payload.scenarios;
        state.scenarioFetchStatus = STATUS.SUCCESS;
        const userSpecificScenario = state.blockScenarioMap[`${state?.modelId}`];
        if (userSpecificScenario && state?.blockId) {
          state.baseScenario =
            state.scenarios &&
            state.scenarios.find((scenario: any) => {
              return scenario?.id === userSpecificScenario;
            });
        } else if (!state.baseScenario) {
          state.baseScenario =
            state.scenarios &&
            state.scenarios.find((scenario: any) => {
              return scenario.is_base;
            });
          if (state?.modelId) state.blockScenarioMap[`${state.modelId}`] = state.baseScenario?.id;
        }
      })
      .addCase(FetchScenarios.rejected, (state) => {
        state.scenarios = [];
      })
      .addCase(UpdateBlockDimension.fulfilled, (state, action) => {
        const block = current(state?.block) || [];
        const updatedDimension = { ...block, dimensions: action.payload?.dimensions };
        state.block = updatedDimension;
      })
      .addCase(FetchPlannerModeBlock.fulfilled, (state, action) => {
        state.block = action.payload;
      })
      .addCase(FetchPlannerModeItems.fulfilled, (state, action) => {
        const actualsData = action.payload?.items?.map((item: any) => {
          let isActual = false;
          if (
            action.payload.actuals[0] + action.payload.actuals[1] === 'FY' &&
            Number(action.payload.actuals[2] + action.payload.actuals[3]) + 2000 >=
              Number(item?.name)
          ) {
            isActual = true;
          } else if (convertToDate(action.payload?.actuals) >= convertToDate(item?.name)) {
            isActual = true;
          }
          return { ...item, isActual };
        });
        state.indicatorHeader = actualsData;
      })
      .addCase(UpdateIndicator.fulfilled, (state, action) => {
        const payload = enrichIndicatorPatchPayload(
          action.payload as Record<string, any> | undefined,
          action.meta?.arg?.indicatorId,
          action.meta?.arg?.data as Record<string, any> | undefined,
        );
        applyIndicatorPatchToState(state, payload, action.meta?.arg?.indicatorId);

        if (!payload) return;

        const patchId = Number(payload.id ?? action.meta?.arg?.indicatorId);
        const indicators = draftArraySnapshot(state.indicators);
        const index = indicators.findIndex((data: any) => data?.id === payload?.id);
        if (index !== -1) {
          const newIndicator = {
            ...state.indicators[index],
            chart_type: payload?.chart_type,
            data_type: payload?.data_type,
            format: payload?.data_format,
            formula: payload?.formula,
            help: payload?.help,
            indicator_type:
              payload?.indicator_type ?? payload?.type ?? state.indicators[index]?.indicator_type,
            is_favourite: payload?.favourite,
            name: payload?.name,
            output_format: payload?.output_format,
            type: payload?.data_format,
          };
          state.indicators[index] = newIndicator;
        }

        // Legacy output indicator list — only when legacy indicators array is in use
        if (Number.isNaN(patchId)) return;

        // Updates Data view for table
        const indicatorsData = draftArraySnapshot<Record<string, any>>(state.indicatorsData);
        const indicatorIndex = indicatorsData.findIndex((data) => data?.id === payload?.id);
        if (indicatorIndex !== -1) {
          const existingRow = indicatorsData[indicatorIndex];
          const indicatorPayload = {
            id: payload?.id,
            warning: payload?.warning,
            type: existingRow?.type,
            'Block indicators': payload?.name,
            Type: payload?.type,
            Formula: payload?.formula ?? '',
            'Help text': payload?.help ?? '',
            Format: payload?.data_format,
            indicator_type: payload?.type,
            'Roll up type': payload?.data_type,
            'Display as output': payload?.favourite,
            'Display as sub-total': payload?.output_format,
            'Default chart type': payload?.chart_type,
          };
          state.indicatorsData[indicatorIndex] = indicatorPayload;
        }
        const blockOutputs: any = draftArraySnapshot(state.indicators);
        const flattenedArray = [];
        for (const object of blockOutputs) {
          if (object?.dim_item_data) {
            flattenedArray.push({ ...object, chevronType: 'parent' });
          } else {
            flattenedArray.push(object);
          }
          if (object?.dim_item_data) {
            const sortedDimItems = [...object.dim_item_data].sort(
              (a: any, b: any) => Number(a?.id ?? 0) - Number(b?.id ?? 0),
            );
            for (const dataObject of sortedDimItems) {
              const childObject = {
                ...dataObject,
                type: object?.type,
                chevronType: 'child',
                total_values: dataObject?.value,
                indicatorId: object?.id,
                parentId: object?.id,
              };
              flattenedArray.push(childObject);
            }
          }
        }
        state.dimensionalIndicators = flattenedArray;
      })
      .addCase(UpdateIndicatorFormat.fulfilled, (state, action) => {
        applyIndicatorPatchToState(
          state,
          action.payload as Record<string, any> | undefined,
          action.meta?.arg?.indicatorId,
        );
      })
      .addCase(UpdateIndicatorProperties.fulfilled, (state, action) => {
        applyIndicatorPatchToState(
          state,
          action.payload as Record<string, any> | undefined,
          action.meta?.arg?.indicatorId,
        );
      })
      .addCase(FetchBlockOutputs.pending, (state) => {
        state.blockOutputsLoading = true;
        state.blockOutputsError = null;
      })
      .addCase(FetchBlockOutputs.rejected, (state, action) => {
        state.blockOutputsLoading = false;
        state.blockOutputsError = action.error.message || 'Failed to fetch block outputs';
      })
      .addCase(FetchBlockOutputs.fulfilled, (state, action) => {
        state.blockOutputsLoading = false;
        state.blockOutputsError = null;
        state.indicators = action.payload;
        const indicatorsArray: any[] = [];
        action.payload.forEach((data: any) => {
          const indicator = {
            id: data?.id,
            warning: data?.warning,
            type: data?.type,
            'Block indicators': data?.name,
            Type: data?.indicator_type,
            Formula: data?.formula ?? '',
            indicator_type: data?.indicator_type,
            'Help text': data?.help ?? '',
            Format: data?.format,
            'Roll up type': data?.data_type,
            'Display as output': data?.is_favourite,
            'Display as sub-total': data?.output_format,
            'Default chart type': data?.chart_type,
          };
          indicatorsArray.push(indicator);
        });
        state.indicatorsData = indicatorsArray;

        const blockOutputs: any = action.payload;
        const flattenedArray = [];
        for (const object of blockOutputs) {
          if (object?.dim_item_data) {
            flattenedArray.push({ ...object, chevronType: 'parent' });
          } else {
            flattenedArray.push(object);
          }
          if (object?.dim_item_data) {
            const dimItems = Array.isArray(object.dim_item_data)
              ? object.dim_item_data
              : Object.values(object.dim_item_data || {});
            for (const dataObject of dimItems) {
              const childObject = {
                ...dataObject,
                type: object?.type,
                chevronType: 'child',
                total_values: dataObject?.value,
                indicatorId: object?.id,
                parentId: object?.id,
              };
              flattenedArray.push(childObject);
            }
          }
        }
        state.dimensionalIndicators = flattenedArray;
        if (state.selectedIndicators) {
          const selectedIndicatorIndex = state.dimensionalIndicators.findIndex(
            (data: any) => data?.id === state.selectedIndicators?.id,
          );
          if (selectedIndicatorIndex !== -1) {
            // state.selectedIndicators = state.dimensionalIndicators[selectedIndicatorIndex];
          }
        }
      })
      .addCase(FetchBlockOutputsV2.pending, (state, action) => {
        const nextBlockId = Number(action.meta?.arg?.blockId);
        if (
          !Number.isNaN(nextBlockId) &&
          state.blockMetadataBlockId != null &&
          state.blockMetadataBlockId !== nextBlockId
        ) {
          state.dimensionalIndicatorsV2 = null;
          state.indicatorsV2 = [];
          state.indicatorsDataV2 = null;
        }
        state.blockOutputsLoadingV2 = true;
        state.blockOutputsErrorV2 = null;
      })
      .addCase(FetchBlockOutputsV2.rejected, (state, action) => {
        state.blockOutputsLoadingV2 = false;
        const payloadMessage = typeof action.payload === 'string' ? action.payload : undefined;
        state.blockOutputsErrorV2 =
          payloadMessage || action.error.message || 'Failed to fetch block outputs';
      })
      .addCase(FetchBlockOutputsV2.fulfilled, (state, action) => {
        state.blockOutputsLoadingV2 = false;
        state.blockOutputsErrorV2 = null;

        const blockOutputs: any[] = action.payload ?? [];

        state.indicatorsV2 = blockOutputs;

        const indicatorsArray: any[] = [];
        blockOutputs.forEach((data: any) => {
          const indicator = {
            id: data?.id,
            warning: data?.warning,
            type: data?.type,
            'Block indicators': data?.name,
            Type: data?.indicator_type,
            Formula: data?.formula ?? '',
            indicator_type: data?.indicator_type,
            'Help text': data?.help ?? '',
            Format: data?.format,
            'Roll up type': data?.data_type,
            'Display as output': data?.is_favourite,
            'Display as sub-total': data?.output_format,
            'Default chart type': data?.chart_type,
          };
          indicatorsArray.push(indicator);
        });
        state.indicatorsDataV2 = indicatorsArray;

        const useArrowParentShell = Boolean(action.meta?.arg?.useArrowResponse);
        const flattenedArray = blockOutputs.flatMap((object: Record<string, any>) =>
          flattenIndicatorOutputsForTable(object, { arrowParentShell: useArrowParentShell }),
        );
        state.dimensionalIndicatorsV2 = flattenedArray;
      })
      .addCase(AddIndicator.fulfilled, (state, action) => {
        const raw = action.payload as Record<string, any> | undefined;
        if (!raw?.id) return;

        const requestedBlockId = action.meta?.arg?.blockId;
        if (
          requestedBlockId != null &&
          raw.block_id != null &&
          Number(requestedBlockId) !== Number(raw.block_id)
        ) {
          return;
        }

        const headers = (state.indicatorHeader ?? []).map((header: any) => ({
          name: header?.name ?? '',
        }));

        if (!state.blockMetadata) {
          state.blockMetadata = { indicators: [] };
        }
        if (!state.blockMetadata.indicators) {
          state.blockMetadata.indicators = [];
        }
        const existsInMetadata = state.blockMetadata.indicators.some(
          (indicator: { id: number }) => indicator.id === raw.id,
        );
        if (!existsInMetadata) {
          state.blockMetadata.indicators.push(mapAddIndicatorResponseToBlockMetadata(raw));
        }

        const existingOutputs = state.dimensionalIndicatorsV2 ?? [];
        const newDimensionalRows = resolveAddIndicatorForDataTable(
          raw,
          headers,
          existingOutputs,
          state.block,
        );
        const outputs = existingOutputs;
        const existsInOutputs = outputs.some(
          (row: any) => row?.id === raw.id && row?.chevronType !== 'child' && !row?.parentId,
        );
        if (!existsInOutputs) {
          state.dimensionalIndicatorsV2 = [...outputs, ...newDimensionalRows];
        }

        const parentFlatRow =
          newDimensionalRows.find((row: any) => row?.chevronType !== 'child') ??
          newDimensionalRows[0];
        if (!parentFlatRow) return;

        const parentOutput = { ...parentFlatRow };
        delete parentOutput.chevronType;
        delete parentOutput.indicatorId;
        delete parentOutput.parentId;

        const blockDimensionIds =
          state.block?.dimensions
            ?.filter((dimension: { name: string }) => dimension.name !== 'Time')
            ?.map((dimension: { id: number }) => dimension.id) ?? [];
        parentOutput.data_input = {
          ...parentOutput.data_input,
          block_id:
            parentOutput.data_input?.block_id ??
            raw.block_id ??
            (requestedBlockId != null ? Number(requestedBlockId) : undefined),
          dimensions:
            blockDimensionIds.length > 0
              ? blockDimensionIds
              : parentOutput.data_input?.dimensions ?? [],
        };

        const indicatorId = raw.id;

        const existsInIndicatorsV2 = (state.indicatorsV2 ?? []).some(
          (indicator: { id: number }) => indicator.id === indicatorId,
        );
        if (!existsInIndicatorsV2) {
          state.indicatorsV2 = [...(state.indicatorsV2 ?? []), parentOutput];
        }

        const existsInIndicators = (state.indicators ?? []).some(
          (indicator: { id: number }) => indicator.id === indicatorId,
        );
        if (!existsInIndicators) {
          state.indicators = [...(state.indicators ?? []), parentOutput];
        }

        const indicatorsDataEntry = {
          id: parentOutput?.id,
          warning: parentOutput?.warning,
          type: parentOutput?.type,
          'Block indicators': parentOutput?.name,
          Type: parentOutput?.indicator_type,
          Formula: parentOutput?.formula ?? '',
          indicator_type: parentOutput?.indicator_type,
          'Help text': parentOutput?.help ?? '',
          Format: parentOutput?.format,
          'Roll up type': parentOutput?.data_type,
          'Display as output': parentOutput?.is_favourite,
          'Display as sub-total': parentOutput?.output_format,
          'Default chart type': parentOutput?.chart_type,
        };
        const existsInIndicatorsDataV2 = (state.indicatorsDataV2 ?? []).some(
          (entry: { id: number }) => entry.id === indicatorId,
        );
        if (!existsInIndicatorsDataV2) {
          state.indicatorsDataV2 = [...(state.indicatorsDataV2 ?? []), indicatorsDataEntry];
        }

        state.selectedIndicators = parentOutput;
      })
      .addCase(GetDependencyIndicator.fulfilled, (state, action) => {
        state.isDependencyArray = action.payload;
      })
      .addCase(FetchFormulaList.fulfilled, (state, action) => {
        state.formulaList = action.payload;
      })
      .addCase(UpdateIndicatorFormula.fulfilled, (state, action) => {
        const indicators = draftArraySnapshot(state.indicators);
        const data = action.payload;
        // Find the index of the item to update
        const index = indicators?.findIndex((item: any) => item?.id === data?.id);
        // If found, update only the calculated_formula key
        if (index !== -1) {
          state.indicators[index] = {
            ...state.indicators[index],
            calculated_formula: { formula_map: data?.calculate_formula, raw_parsed_formula: '' },
            formula: data?.formula,
          };
        }
      });
  },
});

export const getPlannerMode = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice.plannerMode;
export const getPlannerModel = (state: PlannerModeSliceInterface) => state.plannerModeSlice.model;
export const getPlannerBlock = (state: PlannerModeSliceInterface) => state.plannerModeSlice.block;
export const getPlannerBlockMetadata = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice.blockMetadata;
export const getPlannerBlockMetadataBlockId = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice.blockMetadataBlockId;
export const getPlannerBlockMetadataScenarioId = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice.blockMetadataScenarioId;
export const getPlannerBlockMetadataLoading = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice.blockMetadataLoading;
export const getPlannerBlockMetadataError = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice.blockMetadataError;
export const getIndicatorHeaders = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice.indicatorHeader;
export const getDimensionalIndicator = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice.dimensionalIndicators;
export const getDimensionalIndicatorV2 = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice.dimensionalIndicatorsV2;
export const getIndicatorsData = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice.indicatorsData;
export const getIndicatorsDataV2 = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice.indicatorsDataV2;
export const getIndicators = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice.indicators;
export const getIndicatorsV2 = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice.indicatorsV2;
export const getSelectedCell = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice.selectedCell;
export const getSelectedIndicators = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice.selectedIndicators;
export const getSelectedIndicatorType = (state: PlannerModeSliceInterface) =>
  getToolbarIndicatorType(state.plannerModeSlice.selectedIndicators);
export const getIsCalculationSelected = (state: PlannerModeSliceInterface) =>
  getSelectedIndicatorType(state) === 'calculation';
export const getAddIndicator = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice.addIndicator;
export const getToggleInputPanel = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice.toggleInputPanel;
export const getToolbarType = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice.toolbarType;
export const getAllScenarios = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice?.scenarios;
export const getBaseScenario = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice?.baseScenario;
export const getFormulaBarStatus = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice?.formulaBarChange;
export const getDataInput = (state: PlannerModeSliceInterface) => state.plannerModeSlice?.dataInput;
export const getFormulaList = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice?.formulaList;
export const getExpandValue = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice?.isExpand;
export const getDependencyArray = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice?.isDependencyArray;
export const getBlockOutputsLoading = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice?.blockOutputsLoading;
export const getBlockOutputsError = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice?.blockOutputsError;
export const getBlockOutputsLoadingV2 = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice?.blockOutputsLoadingV2;
export const getBlockOutputsErrorV2 = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice?.blockOutputsErrorV2;

export const {
  ResetTable,
  resetBlockTableData,
  toggleToolbarType,
  handlePlannerMode,
  handleSelectedCell,
  handleAddIndicator,
  selectBaseScenario,
  handleToggleInputPanel,
  setExpand,
  handleLocallyAddIndicator,
  handleSelectedCellLocally,
  injectBuilderArrowDimChildren,
  appendBuilderArrowDimChildren,
} = PlannerModeSlice.actions;

export default PlannerModeSlice.reducer;
